<template>
	<div style="width: 100%">
		<Banner v-if="banner" :banner="banner" />
		<div
			class="session-card__wrapper"
			style="padding: 12px 0"
			v-bind:style="{ marginTop: banner ? '8px' : '0' }"
		>
			<ProfileBadge
				v-if="agendaEvent && agendaEvent.sponsored_by"
				isPin
				:pin="{ icon: sponsoredStarIcon, text: sponsoredLabel }"
			/>

			<!-- text -->
			<div
				v-if="agendaEvent && agendaEvent.events_agenda_text"
				class="session-card-title mb-3 px-3"
				v-bind:style="{ paddingTop: agendaEvent.sponsored_by ? '14px' : '0' }"
			>
				{{ agendaEvent.events_agenda_text }}
			</div>

			<div
				v-if="agendaEvent && agendaEvent.events_agenda_from && agendaEvent.events_agenda_to"
				class="session-card-date mb-2 px-3 d-flex align-end"
			>
				<Icon :size="16" type="union" color="var(--accent-color)" />
				<span class="session-card-date ml-2">
					{{ formatDateDay(agendaEvent.events_agenda_from, agendaEvent.agendaTimezone) }}
					•
					{{ formatTime(agendaEvent.events_agenda_from, agendaEvent.agendaTimezone) }} -
					{{ formatTime(agendaEvent.events_agenda_to, agendaEvent.agendaTimezone) }}
					{{ getTimezoneAbbreviation(agendaEvent.agendaTimezone) }}
				</span>
			</div>

			<!-- location -->
			<div
				class="session-card-location mb-2 px-3 cursor-pointer"
				v-if="agendaEvent && agendaEvent.session_location"
				@click="toggleLink(agendaEvent.session_location)"
			>
				<Icon type="pin" :size="16" color="var(--location-color)" :pr="8" />
				{{ agendaEvent.session_location }}
			</div>
			<div
				v-if="metadata && agendaEvent.agenda_link"
				class="post-preview-card__link-wrapper mt-3 mb-3"
			>
				<div
					@click="toggleLink(agendaEvent.agenda_link)"
					class="post-preview-card__link-wrapper__link-box cursor-pointer"
				>
					<div>
						<div class="d-flex align-center">
							<img
								v-if="metadata.image"
								class="post-preview-card__link-wrapper__link-box__image"
								:src="metadata.image"
								alt="link preview"
							/>
							<h1 class="post-preview-card__link-wrapper__link-box__title ml-1">
								{{ agendaEvent.agenda_title || 'Visit Our Website' }}
							</h1>
						</div>
						<div class="post-preview-card__link-wrapper__link mt-1">
							{{ metadata.description }}
						</div>
					</div>
					<div class="d-flex">
						<Icon type="chevron_right" :size="20" color="#9B9B9B" />
					</div>
				</div>
			</div>
			<div
				v-if="registerLimit !== null && agendaEvent && agendaEvent.session_register_link"
				class="session-card__wrapper__progress-container"
			>
				<span class="session-card__wrapper__progress-container__title">{{
					progressText
				}}</span>
				<v-progress-linear
					height="50"
					rounded
					:color="progressColor"
					:value="(registerCount / registerLimit) * 100"
					background-color="#F3F3F6"
					class="session-card__wrapper__progress-container__progress-bar"
				>
					<span class="session-card__wrapper__progress-container__text"
						>{{ registerCount }} of {{ registerLimit }} seats booked</span
					>
				</v-progress-linear>
			</div>
			<div v-if="agendaEvent && agendaEvent.session_register_link">
				<div
					v-if="agendaEvent.session_register_link.startsWith('https')"
					class="session-card__wrapper__buttons-container"
				>
					<button
						@click="externalRegisterSession"
						v-if="isRegister === 0 && !isFullyBooked"
						type="button"
						class="session-card__wrapper__buttons-container__register-session-button"
					>
						Register
					</button>
					<button
						v-if="isRegister === 1 || isFullyBooked"
						:disabled="isRegister === 1"
						type="button"
						class="session-card__wrapper__buttons-container__registered-button"
					>
						{{ isFullyBooked ? 'Fully Booked' : 'Registered' }}
					</button>
				</div>
				<div
					v-if="!agendaEvent.session_register_link.startsWith('http')"
					class="session-card__wrapper__buttons-container mt-3"
				>
					<button
						@click="postSessionRegister"
						v-if="isRegister === 0 && !isFullyBooked"
						type="button"
						class="session-card__wrapper__buttons-container__register-session-button"
					>
						Register
					</button>
					<button
						v-if="isRegister === 1 || isFullyBooked"
						type="button"
						:disabled="isFullyBooked"
						:class="
							isFullyBooked
								? 'session-card__wrapper__buttons-container__registered-session-button__fully-booked'
								: 'session-card__wrapper__buttons-container__registered-session-button'
						"
						@click="delRegisterSession"
					>
						{{ isFullyBooked ? 'Fully Booked' : 'Registered' }}
					</button>
				</div>
			</div>
		</div>
		<div class="session-card__wrapper__button-add-to-sessions" @click="toggleMyAgenda">
			<Icon :type="isScheduled.type" :color="isScheduled.color" :size="24" />
			<span
				:style="{ color: isScheduled.color }"
				class="session-card__wrapper__button-add-to-sessions__title"
				>{{ isScheduled.text }}</span
			>
		</div>
		<!--	description	   -->
		<div v-if="agendaEvent && agendaEvent.session_description" class="session-card__wrapper">
			<span class="session-card-title">Details</span>
			<v-card-text
				ref="contentInner"
				class="event-card__description text-decoration-none"
				v-html="postDescription(description) || ''"
				:class="{ collapsed: !isExpanded && isContentTooLong }"
			/>
			<div v-if="!isExpanded && isContentTooLong" class="fade-overlay"></div>
			<div v-if="isContentTooLong" class="event-card__description__show-more">
				<button @click="toggleExpand">
					{{ isExpanded ? 'Hide' : 'Show More' }}
				</button>
			</div>
		</div>

		<!-- speakers -->
		<div class="session-card__wrapper" v-if="agendaEvent && agendaEvent.speakers.length > 0">
			<div class="d-flex align-center justify-space-between mb-4">
				<h4 class="session-card-title">Speakers</h4>
				<div class="session-card__link-wrapper">
					<router-link
						:to="{
							name: 'event-session-speakers',
							params: {
								eventId: agendaEvent.events_id,
								sessionId: agendaEvent.events_agenda_id,
							},
						}"
						class="session-card__link pt-2 text-decoration-none"
					>
						<span class="session-card__link-wrapper__title">See All</span>
					</router-link>
				</div>
			</div>

			<router-link
				:to="{
					name: 'speaker-by-id',
					params: { id: +speaker_id },
				}"
				v-for="{
					speaker_logo,
					speaker_name,
					speaker_title,
					speaker_id,
				} in agendaEvent.speakers.slice(0, 3)"
				:key="speaker_id"
				class="session-card-speaker"
			>
				<Avatar class="mr-8 post-preview-card__avatar" :size="50" :avatar="speaker_logo" />
				<p v-if="speaker_name" class="session-card-speaker-name">
					{{ speaker_name }}
				</p>
			</router-link>
		</div>
		<div
			v-if="agendaEvent && agendaEvent.sessionActions && agendaEvent.sessionActions.length"
			class="session-card__wrapper"
		>
			<div class="session-card__wrapper__useful">
				<span class="session-card__wrapper__useful__title">Useful Information</span>
				<div>
					<div class="event-card__event-actions mt-3">
						<ActionPreview
							v-for="(action, index) in this.agendaEvent.sessionActions"
							:key="index"
							class="event-card__event-actions__event-wrapper"
							:action="action"
							:event-id="agendaEvent.events_id || $route.params.eventId"
							@click.native="handleEventActionClick(action, $event)"
						>
							<div class="event-card__action-wrapper">
								<Icon
									:type="getAction(action).icon"
									color="var(--accent-color)"
									:size="24"
								/>
							</div>
							<div class="event-card__event-actions__title text-decoration-none">
								{{ getAction(action).name }}
							</div>
						</ActionPreview>
					</div>
				</div>
			</div>
		</div>
		<div
			class="session-card__wrapper"
			v-if="agendaEvent && agendaEvent.session_qa_is_available"
		>
			<!-- questions -->
			<div class="d-flex align-center justify-space-between mb-2">
				<h4 class="session-card-title">Top 3 Session Question(s)</h4>
				<div class="session-card__link-wrapper">
					<router-link
						:to="{
							name: 'event-session-questions',
							params: {
								id: this.$route.params.id,
							},
						}"
					>
						<button
							type="button"
							v-if="!agendaEvent.session_question_answers.length"
							class="session-card__button elevation-0"
						>
							Add a Question
						</button>
						<button v-else type="button" class="session-card__button elevation-0">
							View More/Add
						</button>
					</router-link>
				</div>
			</div>
			<div v-if="agendaEvent && agendaEvent.session_question_answers.length">
				<div
					v-for="question in agendaEvent.session_question_answers"
					:key="question.session_question_answer_id"
					class="session-card__question"
				>
					<span class="session-card__question-text">
						{{ question.session_question_answer_text }}
					</span>
					<span
						:class="{
							'session-card__question-count d-flex align-center': true,
							'session-card__question-count-active': question.upvote,
						}"
					>
						<Icon
							class="mr-2"
							type="like"
							:size="24"
							:color="
								question.upvote ? 'var(--button-main-background-color)' : '#697583'
							"
							:pr="5"
						/>
						{{ question.session_question_count }}
					</span>
				</div>
			</div>
		</div>

		<div
			class="session-card__wrapper"
			v-if="agendaEvent && agendaEvent.polls && agendaEvent.polls.length"
		>
			<h4 class="session-card-title mb-3">Survey(s)</h4>
			<PollEvent
				v-for="poll in polls"
				:key="poll.poll_id"
				:poll="poll"
				:answers="polls_answers"
				:isEventVoteDirty="isVoteDirty"
				:pollsLoading="pollsLoading"
				@answerChange="answerChange"
			/>
			<v-btn
				v-if="!isPollsVoted"
				class="event-card__poll-btn"
				:class="{ 'event-card__poll-btn-disabled': voteDisabled }"
				:ripple="false"
				@click="handleVote"
				:loading="pollsLoading"
			>
				Vote
			</v-btn>
		</div>

		<div class="session-card__wrapper" v-if="ratings.length">
			<h4 class="session-card-title mb-3">Rating(s)</h4>
			<Rating
				v-for="rating in ratings"
				:key="rating.rating_id"
				:rating="rating"
				@ratingChange="ratingChange"
			/>
			<v-btn
				v-if="isRatingsAvailable"
				class="event-card__rating-btn"
				:class="{ 'event-card__rating-btn-disabled': ratingDisabled }"
				:ripple="false"
				@click="handleRate"
				:loading="ratingsLoading"
			>
				Send Rating
			</v-btn>
		</div>
		<v-dialog
			v-model="mobileLink"
			class="exhibitor-page__modal"
			max-width="588px"
			width="100%"
			persistent
		>
			<v-card class="exhibitor-page__modal">
				<div class="exhibitor-page__modal__title">
					This screen is not available in the Web App, but you can access it through the
					mobile app
				</div>
				<button
					type="button"
					@click="toggleCloseMobileLink"
					class="exhibitor-page__modal__btn"
				>
					OK
				</button>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import Avatar from '@/components/Avatar.vue'
import SessionAttachments from '@/components/Resource/SessionAttachments.vue'
import sponsoredStarIcon from '@/assets/images/icon-sponsored-star.svg'
import Poll from '@/components/Post/Poll'
import Rating from '@/components/Event/Rating'
import ProfileBadge from '@/components/ProfileBadge.vue'
import Banner from '@/components/Banner'
import { mapActions, mapGetters } from 'vuex'
import PollEvent from '@/components/Post/PollEvent.vue'
import ActionPreview from '@/components/Event/Action/ActionPreview.vue'
import linkifyThreadText from '@/mixins/linkifyThreadText'

Vue.use(VueScrollTo)

export default {
	name: 'SessionCard',
	components: {
		SessionAttachments,
		Avatar,
		Poll,
		Rating,
		ProfileBadge,
		Banner,
		PollEvent,
		ActionPreview,
	},
	mixins: [linkifyThreadText],
	props: {
		agendaEvent: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			mobileLink: false,
			sponsoredStarIcon,
			polls_answers: [],
			isVoteDirty: false,
			rating_answers: [],
			isRegister: 0,
			registerCount: null,
			registerLimit: null,
			metadata: null,
			isContentTooLong: false,
			isExpanded: false,
			description: '',
		}
	},
	watch: {
		agendaEvent: {
			handler(newVal) {
				if (newVal) {
					this.isRegister = newVal.is_registered
					this.registerLimit = newVal.session_limited
					this.registerCount = newVal.registered_count
				}
			},
			deep: true,
			immediate: true,
		},
		'agendaEvent.agenda_link': {
			handler(newVal) {
				if (newVal) {
					this.fetchMetadata()
				}
			},
			immediate: false,
		},
		'agendaEvent.session_description'(val) {
			if (val) {
				this.$nextTick(this.checkContentHeight)
			}
		},
		description(val) {
			if (val) {
				this.$nextTick(() => {
					this.parseDescription()
					this.checkContentHeight()
				})
			}
		},
	},
	mounted() {
		if (this.agendaEvent && this.agendaEvent.agenda_link) {
			this.fetchMetadata()
		}
		this.$nextTick(() => {
			this.checkContentHeight()
		})
	},
	created() {
		if (this.agendaEvent && this.agendaEvent.session_description) {
			this.parseDescription()
		}
	},

	methods: {
		...mapActions('event', [
			'addMyAgenda',
			'removeMyAgenda',
			'voteEventPolls',
			'voteSessionRatings',
			'postRegisterSession',
			'deleteRegisterSessions',
		]),
		formatDateDay(timestamp, timezone) {
			const date = new Date(timestamp * 1000)
			const offset = Number(timezone.timezone_utc.slice(-2))
			const sign = timezone.timezone_utc.slice(3, 4)

			if (sign === '-') {
				date.setHours(date.getHours() - offset)
			} else {
				date.setHours(date.getHours() + offset)
			}
			return date.toLocaleDateString('en-US', {
				weekday: 'short',
				month: 'short',
				day: 'numeric',
			})
		},
		formatTime(timestamp, timezone) {
			const options = {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: 'UTC',
			}

			const date = new Date(timestamp * 1000)
			const offset = Number(timezone.timezone_utc.slice(-2))
			const sign = timezone.timezone_utc.slice(3, 4)

			if (sign === '-') {
				date.setHours(date.getHours() - offset)
			} else {
				date.setHours(date.getHours() + offset)
			}

			return date.toLocaleTimeString('en-US', options)
		},
		getTimezoneAbbreviation(timezone) {
			return timezone.timezone_code || 'UTC'
		},
		parseDescription() {
			const parser = new DOMParser()
			const doc = parser.parseFromString(this.agendaEvent.session_description, 'text/html')
			this.description = doc.body.innerHTML
		},
		toggleCloseMobileLink() {
			this.mobileLink = false
		},
		async toggleLink(link) {
			const target = link

			if (!target) return

			if (this.isUnsupportedDeepLink(target)) {
				this.mobileLink = true
				return
			}
			if (this.isMapUrlValid(target)) {
				try {
					await this.getMapRegisterToken()
					if (!this.mapRegisterToken) {
						console.error('Token not received')
						return
					}

					const urlWithToken = `${target}${target.includes('?') ? '&' : '?'}code=${
						this.mapRegisterToken
					}`
					window.open(urlWithToken, '_blank')
				} catch (e) {
					console.error(e)
				}
			} else {
				window.open(target, '_blank')
			}
		},
		async handleEventActionClick(eventAction, event) {
			if (eventAction.action_type === 'link') {
				try {
					await this.toggleLink(eventAction.action_value)
					return
				} catch (e) {
					console.error(e)
				}
			}
			if (eventAction.action_type === 'deepLink') {
				const restrictedLinks = [
					'live-events',
					'event-manager',
					'higher-logic',
					'gamification',
				]
				const lowerCaseLink = eventAction.action_value.toLowerCase()
				const isRestricted = restrictedLinks.some((restricted) =>
					lowerCaseLink.includes(restricted)
				)

				if (isRestricted) {
					if (event) {
						event.preventDefault()
						event.stopPropagation()
					}
					this.mobileLink = true
					return
				} else {
					const target = this.transformDeepLink(eventAction.action_value)
					window.open(target, '_blank')
					return
				}
			}
		},
		getAction(action) {
			let icons = {
				link: 'link',
				email: 'email',
				phone: 'phone',
				grid: 'phone_empty',
				fileList: 'document',
				linkList: 'list',
				deepLink: 'link',
				grid2: 'IconGrid2',
			}
			return {
				icon: icons[action.action_type],
				link:
					action.action_type !== '' && action.action_type !== ''
						? action.action_value
						: null,
				name: action.action_name,
				color: action.button_color,
			}
		},
		transformDeepLink(link) {
			const match = link.match(/:\/\/(.*)/)
			return match ? `${window.location.origin}/webapp/${match[1]}` : link
		},
		toggleExpand() {
			this.isExpanded = !this.isExpanded
		},
		checkContentHeight() {
			const contentEl = this.$refs.contentInner
			if (contentEl) {
				const lineHeight = parseFloat(window.getComputedStyle(contentEl).lineHeight)
				const height = contentEl.scrollHeight
				const numLines = Math.floor(height / lineHeight)
				this.isContentTooLong = numLines > 5
			}
		},
		async fetchMetadata() {
			try {
				const response = await fetch(this.agendaEvent.agenda_link, { mode: 'cors' })
				const html = await response.text()

				const parser = new DOMParser()
				const doc = parser.parseFromString(html, 'text/html')

				this.metadata = {
					description:
						doc.querySelector("meta[property='og:description']")?.content ||
						this.getDisplayLink(this.agendaEvent.agenda_link),
					image: doc.querySelector("meta[property='og:image']")?.content || null,
					url: this.agendaEvent.agenda_link,
				}
			} catch (error) {
				console.error(error)
				this.metadata = {
					description: this.getDisplayLink(this.agendaEvent.agenda_link),
					image: null,
					url: this.agendaEvent.agenda_link,
				}
			}
		},
		getDisplayLink(rawLink) {
			if (!rawLink) {
				return ''
			}
			if (rawLink.startsWith('com.pinxterapp.dev.clowder-core://')) {
				return window.location.origin
			}
			try {
				const urlObj = new URL(rawLink)
				let domain = urlObj.hostname
				if (!domain.startsWith('www.')) {
					domain = 'www.' + domain
				}
				return domain
			} catch (error) {
				return rawLink
			}
		},
		externalRegisterSession() {
			window.location.href = this.agendaEvent.session_register_link
		},
		async postSessionRegister() {
			if (this.registerCount < this.registerLimit || this.registerLimit === null) {
				this.isRegister = 1
				this.registerCount++
				await this.postRegisterSession({
					session_id: this.agendaEvent.events_agenda_id,
					user_id: this.profile.user_id,
					description: null,
				})
			}
		},

		async delRegisterSession() {
			if (this.registerCount > 0 || this.registerLimit === null) {
				this.isRegister = 0
				this.registerCount--
				await this.deleteRegisterSessions(this.agendaEvent.events_agenda_id, {
					session_id: this.agendaEvent.events_agenda_id,
					user_id: this.profile.user_id,
					description: null,
				})
			}
		},

		toggleMyAgenda() {
			if (!this.isMyAgendaLoading) {
				if (this.agendaEvent.is_scheduled) {
					this.removeMyAgenda({
						id: this.agendaEvent.events_agenda_id,
						myAgenda: false,
						single: true,
					})
				} else {
					this.addMyAgenda({ id: this.agendaEvent.events_agenda_id, single: true })
				}
			}
		},
		answerChange(answer) {
			if (!answer.answer || (Array.isArray(answer.answer) && !answer.answer.length)) {
				if (this.polls_answers.length) {
					this.polls_answers = this.polls_answers.filter(
						(poll) => poll.poll_id !== answer.poll_id
					)
				}
			} else {
				const pollAnswer = this.polls_answers.find(
					(poll) => poll.poll_id === answer.poll_id
				)
				if (pollAnswer) {
					this.polls_answers = this.polls_answers.map((poll) => {
						if (poll.poll_id === answer.poll_id) {
							return answer
						} else {
							return poll
						}
					})
				} else {
					this.polls_answers = [...this.polls_answers, answer]
				}
			}
		},
		preparePollsPayload() {
			return this.polls_answers
				.map((item) => {
					let answerIds = Array.isArray(item.answer)
						? item.answer.filter((v) => v !== 'custom')
						: typeof item.answer === 'number'
						? [item.answer]
						: []

					const customPicked =
						(Array.isArray(item.answer) && item.answer.includes('custom')) ||
						item.answer === 'custom'

					let payload = { poll_id: item.poll_id }

					if (answerIds.length) payload.poll_answer_id = answerIds
					if (customPicked && item.customAnswer && item.customAnswer.trim().length)
						payload.custom_answer = item.customAnswer.trim()
					if (
						answerIds.length ||
						(payload.custom_answer && payload.custom_answer.length)
					) {
						return payload
					}
					return null
				})
				.filter(Boolean)
		},
		async handleVote() {
			this.isVoteDirty = true
			if (!this.voteDisabled) {
				const answersPayload = this.preparePollsPayload()
				await this.voteEventPolls({
					answers: answersPayload,
					eventId: null,
					sessionId: this.agendaEvent.events_agenda_id,
				})
			}
		},
		ratingChange(rating) {
			if (this.rating_answers.find((item) => item.rating_id === rating.rating_id)) {
				this.rating_answers = this.rating_answers.map((item) => {
					if (item.rating_id === rating.rating_id) {
						return {
							...rating,
						}
					} else {
						return item
					}
				})
			} else {
				this.rating_answers = [...this.rating_answers, rating]
			}
		},
		async handleRate() {
			if (!this.ratingDisabled) {
				await this.voteSessionRatings({
					ratings: this.rating_answers,
					sessionId: +this.$route.params.sessionId,
				})
			}
		},
	},
	computed: {
		...mapGetters('event', [
			'isMyAgendaLoading',
			'pollsLoading',
			'ratingsLoading',
			'isLoading',
		]),
		...mapGetters('advertisement', ['banner']),
		...mapGetters('auth', {
			profile: 'profile',
			isAuthenticated: 'isAuthenticated',
			authIsLoading: 'isLoading',
		}),
		isFullyBooked() {
			return this.registerLimit === this.registerCount && this.isRegister === 0
		},
		progressText() {
			const percentage = (this.registerCount / this.registerLimit) * 100
			if (percentage >= 100) {
				return 'Limited Seats!'
			} else if (percentage >= 81) {
				return 'Register Now – Limited Seats!'
			} else {
				return "Secure your spot before it's gone!"
			}
		},
		progressColor() {
			const percentage = (this.registerCount / this.registerLimit) * 100
			if (percentage >= 100) {
				return 'rgba(255, 59, 48, 0.3)'
			} else if (percentage >= 81) {
				return 'rgba(236, 149, 47, 0.3)'
			} else {
				return 'rgba(16, 196, 145, 0.3)'
			}
		},

		polls() {
			if (!this.agendaEvent || !Array.isArray(this.agendaEvent.polls)) return []
			return this.agendaEvent.polls.filter(
				(poll) =>
					poll.pollsAnswers.length ||
					(!poll.pollsAnswers.length && poll.allow_custom_answer)
			)
		},
		voteDisabled() {
			return (
				this.pollsLoading ||
				this.polls_answers.length !== this.polls.length ||
				this.polls_answers.some((poll) => poll.error)
			)
		},
		isPollsVoted() {
			return this.agendaEvent.polls.every((poll) => poll.vote)
		},
		sponsoredLabel() {
			return this.agendaEvent && this.agendaEvent.sponsored_by
				? `Sponsored by ${this.agendaEvent.sponsored_by}`
				: ''
		},
		isScheduled() {
			return this.agendaEvent && this.agendaEvent.is_scheduled
				? {
						text: 'Remove from My Sessions',
						color: '#9B9B9B',
						type: 'remove_schedule',
				  }
				: {
						text: 'Add to My Sessions',
						color: 'var(--accent-color)',
						type: 'add_schedule',
				  }
		},
		ratings() {
			return Array.isArray(this.agendaEvent?.ratings)
				? this.agendaEvent.ratings.filter(
						(rating) => !rating.vote || (rating.vote && rating.display_rating)
				  )
				: []
		},
		isRatingsAvailable() {
			return this.ratings.some((rating) => !rating.vote)
		},
		availableRatings() {
			return this.ratings.filter((rating) => !rating.vote)
		},
		ratingDisabled() {
			return this.rating_answers.length !== this.availableRatings.length
		},
	},
}
</script>

<style lang="scss">
.session-card {
	font-family: Inter, sans-serif;
	&__wrapper {
		position: relative;
		background-color: #fff;
		padding: 12px 12px;
		border-radius: 8px;
		margin-bottom: 8px;
		border: 1px solid #e2e2e2;
		&__button-add-to-sessions {
			width: 100%;
			height: 75px;
			background-color: #ffffff;
			border: 1px solid #e2e2e2;
			border-radius: 8px;
			margin-bottom: 8px;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			flex-direction: column;
			&__title {
				font-family: 'Inter', sans-serif;
				font-weight: 500;
				font-size: 16px;
				line-height: 100%;
				letter-spacing: 0;
				color: var(--accent-color);
				margin-top: 8px;
			}
		}
		&__useful {
			&__title {
				font-family: Inter, sans-serif;
				font-weight: 700;
				font-size: 18px;
				line-height: 100%;
				letter-spacing: 0;
				color: var(--accent-color);
			}
			&__info-container {
				&__section {
					display: flex;
					align-items: center;
					max-width: 261px;
					width: 100%;
					padding: 8px;
					background-color: #f3f3f6;
					border-radius: 8px;
					&__title {
						font-family: Inter, sans-serif;
						font-weight: 400;
						font-size: 16px;
						line-height: 100%;
						letter-spacing: 0;
						color: #4a4a4a !important;
						margin-left: 8px;
					}
				}
			}
		}
		&__progress-container {
			width: 100%;
			padding: 0 16px;
			display: flex;
			flex-direction: column;
			align-items: center;
			&__title {
				font-family: 'Inter', sans-serif;
				font-weight: 600;
				font-size: 14px;
				color: #002855;
				line-height: 22px;
				margin-bottom: 8px;
			}
			&__progress-bar {
				position: relative;
				display: flex;
				align-items: center;
				justify-content: center;
			}
			&__text {
				position: absolute;
				width: 100%;
				text-align: center;
				font-family: 'Inter', sans-serif;
				font-weight: 600;
				font-size: 16px;
				color: #002855;
			}
		}
		&__buttons-container {
			width: 100%;
			padding: 12px 12px 0 12px;
			border-top: 1.5px solid #e2e2e2;
			display: flex;
			justify-content: center;
			&__registered-button {
				width: 100%;
				height: 50px;
				border-radius: 8px;
				align-items: center;
				border: 2px solid #9b9b9b;
				color: #697583;
				font-family: 'Inter', sans-serif;
				font-size: 18px;
				font-weight: 700;
				line-height: 100%;
			}
			&__register-session-button {
				width: 100%;
				height: 50px;
				border-radius: 8px;
				align-items: center;
				background-color: var(--button-secondary-background-color);
				color: #ffffff;
				font-family: 'Inter', sans-serif;
				font-size: 18px;
				font-weight: 700;
				line-height: 100%;
			}
			&__registered-session-button {
				width: 100%;
				height: 50px;
				border-radius: 8px;
				align-items: center;
				border: 2px solid var(--button-secondary-background-color);
				color: var(--button-secondary-background-color);
				font-family: 'Inter', sans-serif;
				font-size: 18px;
				font-weight: 700;
				line-height: 100%;
				&__fully-booked {
					width: 100%;
					height: 50px;
					border-radius: 8px;
					align-items: center;
					border: 2px solid #9b9b9b;
					color: #9b9b9b;
					font-family: 'Inter', sans-serif;
					font-size: 18px;
					font-weight: 700;
					line-height: 100%;
				}
			}
		}
	}

	&-date {
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
		color: #4a4a4a;
	}

	&-title {
		color: var(--accent-color);
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
	}

	&-text {
		color: var(--accent-color);
		font-size: 14px;
		line-height: 17.07px;

		p {
			margin: 0;
		}
	}

	&-tag {
		display: flex;
		align-items: center;
		font-size: 13px;
		font-weight: 700;
		line-height: 19.37px;
	}

	&-location {
		display: flex;
		align-items: center;
		color: var(--location-color);
		font-size: 13px;
		font-weight: 500;
		line-height: 19.37px;
	}

	&-speaker {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		text-decoration: none;
		background-color: #f3f3f6;
		padding: 12px;
		border-radius: 8px;

		&:not(:last-child) {
			margin-bottom: 8px;
		}

		&-name {
			color: var(--accent-color);
			margin: 0 !important;
			flex-grow: 1;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
		}
		&-title {
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a !important;
			margin-top: 4px;
		}
	}

	&__link {
		font-size: 14px;
		font-weight: 700;
		line-height: 17.07px;
		color: var(--button-main-background-color) !important;

		&-wrapper {
			display: flex;
			justify-content: center;
			&__title {
				font-family: 'Inter', sans-serif;
				font-weight: 700;
				font-size: 18px;
				line-height: 100%;
				letter-spacing: 0;
				text-align: center;
			}
		}
	}

	&__button {
		color: var(--button-main-background-color) !important;
		background-color: transparent !important;
		text-transform: capitalize;
		text-decoration: none;
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
	}

	&-registered {
		padding: 0 30px;

		&-chip {
			height: 23px !important;
			font-size: 12px !important;
			line-height: 14.63px;
			font-weight: 700;
			background-color: var(--accent-color) !important;
			color: #fff !important;
		}
	}

	&-add-my-agenda {
		&-wrapper {
			padding: 20px 16px 4px 16px;
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
		}

		&-btn {
			text-transform: none;
			font-size: 14px;
			font-weight: 700;
			line-height: 17.07px;
			color: #0055a3;
			background-color: transparent !important;
			padding: 0 8px !important;

			&-text {
				text-decoration: underline;
			}
		}
	}

	&__add-question-btn,
	&__add-question-btn-view {
		background: var(--button-main-background-color) !important;
		color: var(--button-main-color) !important;
		text-transform: capitalize;
		font-size: 16px !important;
		font-weight: 700;
	}

	&__add-question-btn-view {
		color: var(--accent-color) !important;
		background: transparent !important;
	}

	&__question {
		display: flex;
		align-items: center;
		flex-wrap: nowrap;
		justify-content: space-between;
		padding: 12px;
		background-color: #f3f3f6;
		border-radius: 8px;
		height: 58px;
		width: 100%;

		&:not(:last-child) {
			margin-bottom: 8px;
		}

		&-text {
			font-family: 'Inter', sans-serif;
			font-weight: 500;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--accent-color);
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}

		&-count {
			font-family: 'Inter', sans-serif;
			font-weight: 500;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: #697583;

			&-active {
				color: var(--button-main-background-color);
			}
		}
	}
}

.file_box {
	background: #f3f3f6;
	padding: 12px;
	border-radius: 12px;
	gap: 12px;
}
.event-card__description.collapsed {
	display: -webkit-box;
	-webkit-line-clamp: 5;
	-webkit-box-orient: vertical;
	overflow: hidden;
}
.fade-overlay {
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 30px;
	pointer-events: none;
	z-index: 0;
}
</style>
